import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Search, FileJson, FileText, Microscope } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { OmCard, OmHead, OmKpi, OmTable, Row, Td, Pill, GOLD, MUTED, FIELD } from "@/components/om/OmBits";
import { downloadRca } from "@/lib/rca";

const TONE = { Approved: "good", "Under Review": "warn", Draft: "gold", Rejected: "bad" };
const when = (v) => (v ? new Date(v).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—");

export default function OmRcaLibrary() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [f, setF] = useState({ category: "", status: "", matched: "", from: "", to: "" });

  useEffect(() => {
    api.get("/om/rca").then(({ data }) => setData(data)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  }, []);

  const cats = useMemo(() => [...new Set((data?.records || []).map((r) => r.asset_category).filter(Boolean))], [data]);
  const rows = useMemo(() => (data?.records || []).filter((r) =>
    (!q || `${r.asset_name} ${r.fault_description} ${r.ticket_code} ${r.fault_name}`.toLowerCase().includes(q.toLowerCase())) &&
    (!f.category || r.asset_category === f.category) &&
    (!f.status || r.status === f.status) &&
    (!f.matched || String(!!r.template_matched) === f.matched) &&
    (!f.from || (r.generated_at || "") >= f.from) &&
    (!f.to || (r.generated_at || "") <= `${f.to}T23:59:59`)), [data, q, f]);

  const grab = async (id, fmt) => {
    try { await downloadRca(id, fmt); toast.success(`RCA ${fmt.toUpperCase()} downloaded`); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="om-rca-library-page">
      <OmHead title="RCA Library" subtitle="Every root cause analysis generated for your company — searchable, filterable and exportable." />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
        <OmKpi label="Total RCAs generated" value={data?.kpi.total ?? "—"} testid="rca-kpi-total" />
        <OmKpi label="Template match rate" value={data ? `${data.kpi.template_match_rate}%` : "—"} testid="rca-kpi-match" />
        <OmKpi label="Approved" value={data?.kpi.approved ?? "—"} testid="rca-kpi-approved" />
        <OmKpi label="Pending review" value={data?.kpi.pending_review ?? "—"} testid="rca-kpi-pending" />
      </div>

      <OmCard className="mb-3" testid="om-rca-filters">
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-3" style={{ color: MUTED }} />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search asset, fault, ticket" className="pl-8 w-[240px]" style={FIELD} data-testid="om-rca-search" />
          </div>
          {[["category", "All categories", cats], ["status", "All statuses", ["Draft", "Under Review", "Approved", "Rejected"]],
            ["matched", "Template match — any", [["true", "Template match"], ["false", "Manual scaffold"]]]].map(([k, placeholder, opts]) => (
            <select key={k} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })}
              className="h-9 px-2 text-[13px] border" style={{ ...FIELD, borderRadius: 6 }} data-testid={`om-rca-filter-${k}`}>
              <option value="">{placeholder}</option>
              {opts.map((o) => Array.isArray(o) ? <option key={o[0]} value={o[0]}>{o[1]}</option> : <option key={o} value={o}>{o}</option>)}
            </select>
          ))}
          <Input type="date" value={f.from} onChange={(e) => setF({ ...f, from: e.target.value })} className="w-[150px]" style={FIELD} data-testid="om-rca-filter-from" />
          <Input type="date" value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} className="w-[150px]" style={FIELD} data-testid="om-rca-filter-to" />
          <Pill tone="gold" testid="om-rca-count">{rows.length} of {data?.records.length || 0}</Pill>
        </div>
      </OmCard>

      <OmCard testid="om-rca-table-card">
        {rows.length === 0 ? (
          <p className="text-[12.5px]" style={{ color: MUTED }} data-testid="om-rca-empty">
            No RCA records yet. A Critical CM ticket drafts one automatically, or use “Generate RCA” on any ticket.
          </p>
        ) : (
          <OmTable head={["Ticket", "Asset", "Fault", "Generated", "Match", "Status", "Reviewed by", "Export"]} testid="om-rca-table">
            {rows.map((r, i) => (
              <Row key={r.id} i={i} testid={`om-rca-row-${r.ticket_code || r.id.slice(0, 6)}`}>
                <Td><Link to={`/om/rca/${r.id}`} className="hover:underline" style={{ color: GOLD }} data-testid={`om-rca-open-${r.ticket_code}`}>{r.ticket_code || "—"}</Link></Td>
                <Td>{r.asset_code} {r.asset_name}</Td>
                <Td><span className="text-white">{r.fault_name}</span><div className="text-[11px]" style={{ color: MUTED }}>{r.asset_category} · {r.fault_category}</div></Td>
                <Td>{when(r.generated_at)}</Td>
                <Td><Pill tone={r.template_matched ? "good" : "warn"}>{r.template_matched ? "Template Match" : "Manual Scaffold"}</Pill></Td>
                <Td><Pill tone={TONE[r.status] || "muted"}>{r.status}</Pill></Td>
                <Td>{r.reviewed_by || "—"}</Td>
                <Td>
                  <div className="flex gap-2">
                    <button onClick={() => grab(r.id, "json")} title="Download JSON" data-testid={`om-rca-json-${r.ticket_code}`}><FileJson className="w-4 h-4" style={{ color: GOLD }} /></button>
                    <button onClick={() => grab(r.id, "pdf")} title="Download PDF" data-testid={`om-rca-pdf-${r.ticket_code}`}><FileText className="w-4 h-4" style={{ color: GOLD }} /></button>
                    <Link to={`/om/rca/${r.id}`} title="Review"><Microscope className="w-4 h-4" style={{ color: MUTED }} /></Link>
                  </div>
                </Td>
              </Row>
            ))}
          </OmTable>
        )}
      </OmCard>
    </div>
  );
}
